import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "annapurna_cart_v1";

// wa.me and tel: links need plain digits (no "+", no spaces) — a "+" or
// space here breaks the WhatsApp deep link and sends it to a 404 page.
export const WHATSAPP_NUMBER = "919281105088";
export const PHONE_NUMBER = "919281105088";
export const PHONE_DISPLAY = "+91 92811 05088";
export const WA_DISPLAY = "+91 92811 05088";

export const INSTAGRAM_URL = "https://www.instagram.com/annapurnahomepickles/";
export const FACEBOOK_URL  = "https://www.facebook.com/profile.php?id=61591001691720";
export const YOUTUBE_URL   = "https://www.youtube.com/channel/UCDUaMzS8s1KMBbriffCe9ew";

export function useCart() {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, size) => {
    setItems((prev) => {
      const key = `${product.id}-${size.label}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          image_url: product.image_url,
          size: size.label,
          price: size.price,
          quantity: 1,
        },
      ];
    });
  }, []);

  // Re-add a past order's items in one go ("Buy Again") — merges quantities
  // into whatever's already in the cart instead of replacing it, same as
  // addItem does for a single item.
  const addItems = useCallback((orderItems) => {
    setItems((prev) => {
      const merged = [...prev];
      orderItems.forEach((it) => {
        const key = `${it.product_id}-${it.size}`;
        const idx = merged.findIndex((i) => i.key === key);
        if (idx >= 0) {
          merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + it.quantity };
        } else {
          merged.push({
            key,
            product_id: it.product_id,
            name: it.name,
            slug: it.slug,
            image_url: it.image_url,
            size: it.size,
            price: it.price,
            quantity: it.quantity,
          });
        }
      });
      return merged;
    });
  }, []);

  // Every combo build is its own cart line, even two builds of the same
  // tier — merging would silently lose one customer's product selection.
  // Key includes a random suffix so identical tiers never collide.
  // totalPrice is the sum of the selected products' individual admin-set
  // prices (different pickles cost different amounts to make, so there's no
  // single flat tier price) — computed by the caller, who already has both
  // the price map and the selections to hand.
  const addComboItem = useCallback((tier, comboType, selections, totalPrice) => {
    setItems((prev) => [
      ...prev,
      {
        key: `combo-${comboType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        item_type: "combo",
        combo_type: comboType,
        name: tier.label,
        image_url: null,
        size: `${tier.itemCount} items`,
        price: totalPrice,
        quantity: 1,
        selections,
      },
    ]);
  }, []);

  // Unlike combos, identical pack types DO merge into one line (key has no
  // uniqueness suffix) — required so the max-quantity cap only ever has to
  // look at a single line per pack type. totalPrice is the sum of every
  // product configured in the pack (all of them are included, unlike combos
  // where the customer picks a subset) — computed by the caller.
  const addSamplePackItem = useCallback((pack, packType, totalPrice) => {
    setItems((prev) => {
      const key = `sample_pack-${packType}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [
        ...prev,
        {
          key,
          item_type: "sample_pack",
          pack_type: packType,
          name: pack.label,
          image_url: null,
          size: pack.fixedSizeLabel || "150g",
          price: totalPrice,
          quantity: 1,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.key !== key)
        : prev.map((i) => (i.key === key ? { ...i, quantity: qty } : i))
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return { items, addItem, addItems, addComboItem, addSamplePackItem, updateQty, removeItem, clear, total, count };
}

// Returns true if adding `additionalQty` more of `packType` would NOT exceed
// the configured max (default 3). Used by the Sample Pack modal (disable Add
// to Cart once at cap), CartSheet's qty stepper, AND CheckoutPage's validate()
// as defense in depth — the RPC is the real source of truth and re-checks
// server-side at order-creation time regardless.
export function validateSamplePackQuantity(items, packType, additionalQty, maxQtyPerOrder = 3) {
  const currentQty = items
    .filter((i) => i.item_type === "sample_pack" && i.pack_type === packType)
    .reduce((sum, i) => sum + i.quantity, 0);
  return currentQty + additionalQty <= maxQtyPerOrder;
}

export function buildWhatsAppLink(items, total, customer = {}) {
  const lines = [];
  lines.push("Hello Annapurna Home Pickles,");
  lines.push("I would like to place an order:");
  lines.push("");
  if (items.length === 0) {
    lines.push("(Please add items to my order)");
  } else {
    items.forEach((it, idx) => {
      lines.push(
        `${idx + 1}. ${it.name} – ${it.size} × ${it.quantity} = ₹${(
          it.price * it.quantity
        ).toFixed(0)}`
      );
    });
    lines.push("");
    lines.push(`Total: ₹${total.toFixed(0)}`);
  }
  if (customer.name) lines.push(`\nName: ${customer.name}`);
  if (customer.phone) lines.push(`Phone: ${customer.phone}`);
  if (customer.address) lines.push(`Address: ${customer.address.replace(/\n+/g, ", ").trim()}`);
  if (customer.notes) lines.push(`Notes: ${customer.notes.replace(/\n+/g, " ").trim()}`);
  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
}

export function formatPrice(n) {
  return `₹${n.toLocaleString("en-IN")}`;
}

// Mirrors the server-side public._parse_weight_grams SQL function — every
// size label in this app ("250gm", "500g", ...) is always specified in
// grams, so this is just "leading number off the label", nothing fancier.
// Client-side use is for an immediate checkout-page preview only; the
// server always recomputes this itself before charging anything.
export function parseWeightGrams(label) {
  const match = String(label || "").match(/^\s*([0-9]+(\.[0-9]+)?)/);
  return match ? Number(match[1]) : 0;
}
